import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Dashboard from './Dashboard';
import ApprovalsPage from './ApprovalsPage';
import WorkOrdersList from './WorkOrdersList';
import TicketsList from './TicketsList';
import SLATrackingDashboard from './SLATrackingDashboard';
import AdminAnalyticsDashboard from './AdminAnalyticsDashboard';
import MonthlyReport from './MonthlyReport';
import ContractorsList from './ContractorsList';
import InvoicingView from '../components/InvoicingView';
import AdvancedSearch from '../components/AdvancedSearch';
import TicketTemplates from '../components/TicketTemplates';
import DocsPage from '../components/docs/DocsPage';
import CatalogueAdmin from './catalogue/CatalogueAdmin';
import './ManageHub.css';

/**
 * TICKET MANAGEMENT, under one roof.
 *
 * Gordon, 21 October, redrawing the administrator's top bar: "Ticket Mgmt"
 * is one word in the bar and opens a hub page with a sub-menu strip like the
 * contractor portal's tabs - Dashboard, Approvals, Work Orders, Tickets,
 * Reporting, Contractors. Everything that used to be its own word in the bar
 * (Dashboard, the Work group, the Reports group, All tickets, Templates,
 * Search) lives here now, and the bar is left with the things everybody has
 * plus three administrative doors: this one, the asset register, Advanced.
 *
 * NOTHING MOVED. Every tab renders the page component that already existed
 * for it, untouched, below the strip. The strip is a row of real links, so
 * each tab has an address (/manage/approvals and so on), the browser's back
 * button works, and the old addresses - /approvals, /work-orders, /tickets,
 * /sla, /admin-analytics, /contractors, /search, /templates - still answer:
 * App.js mounts this page at each of them with the matching tab lit, so a
 * bookmark, a link from the bell or from chat, and every e2e test that types
 * a path still land where they always did.
 *
 * `tab` names the open tab; the route decides it, not a click, because
 * arriving by link, redirect or typed URL all have to look the same.
 */

/* The canonical address of each tab. The old addresses are aliases of these
   (App.js); the strip links here so the hub reads as one place. */
export const MANAGE_TABS = {
  dashboard: '/manage',
  approvals: '/manage/approvals',
  'work-orders': '/manage/work-orders',
  tickets: '/manage/tickets',
  search: '/manage/search',
  templates: '/manage/templates',
  // The service catalogue (124): what can be asked for under Request
  // something - beside Templates, because an item is a ticket made ready to
  // ask for. /manage/catalogue/:id is one item.
  catalogue: '/manage/catalogue',
  sla: '/manage/sla',
  analytics: '/manage/analytics',
  reports: '/manage/reports',
  invoicing: '/manage/invoicing',
  contractors: '/manage/contractors',
  // Contracts (120): a tab of the Contractors page, with its own address.
  contracts: '/manage/contracts',
  // Not /manage/docs: every link inside the docs, the bell's and a ticket's
  // included, is /docs/:id, and for an administrator /docs is this tab
  // (App.js) - so the tab and its pages share one address. /manage/docs
  // answers too, by going there.
  docs: '/docs'
};

const CHEVRON = (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.9"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 6.5 8 10.5 12 6.5" />
  </svg>
);

/**
 * A tab that is a small menu: Tickets and Reporting each hold two or three
 * pages, and Gordon drew them with a chevron. The button carries the active
 * state of whichever child is open, for the same reason the navbar's groups
 * do - a page inside a closed menu cannot show that it is the page you are
 * on. Click to open; Escape or a press outside closes.
 */
function TabMenu({ label, items, current }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const holdsHere = items.some((it) => it.key === current);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="mh-menu" ref={ref}>
      <button
        type="button"
        className={`tab-button mh-menu-btn${holdsHere ? ' active' : ''}`}
        aria-expanded={open}
        aria-haspopup="true"
        aria-current={holdsHere ? 'true' : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
        {CHEVRON}
      </button>
      {open && (
        <div className="mh-menu-panel" role="menu">
          {items.map((it) => (
            <NavLink key={it.key} to={MANAGE_TABS[it.key]} role="menuitem"
                     className={it.key === current ? 'active' : undefined}
                     onClick={() => setOpen(false)}>
              {it.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

/* What each tab shows. The components are the pages that existed before the
   hub; the hub adds nothing to them and takes nothing away. */
const PANES = {
  dashboard: () => <Dashboard />,
  approvals: () => <ApprovalsPage />,
  // Told who is looking rather than always `true`: the named approver who
  // is not an administrator (below) must not be drawn admin controls.
  'work-orders': ({ isAdmin }) => <WorkOrdersList isAdmin={isAdmin} />,
  tickets: () => <TicketsList />,
  search: () => <AdvancedSearch />,
  templates: () => <TicketTemplates />,
  catalogue: () => <CatalogueAdmin />,
  sla: () => <SLATrackingDashboard />,
  analytics: () => <AdminAnalyticsDashboard />,
  // The monthly reporting pack (118): one month, or any range of days, of
  // tickets, SLA, hours, devices and people - to print, download or email.
  reports: () => <MonthlyReport />,
  // Gordon, 23 October: a view for invoicing - the invoices, filtered and
  // sorted by contractor, each opening onto its tickets with the budget and
  // the hours worked and billed. Under Reporting with SLA and Analytics: it is
  // read, not worked in, and it is administrators only, as invoices are.
  invoicing: () => (
    <div className="container">
      <div className="page-bar">
        <div className="page-bar-title"><h1 className="page-title">Invoicing</h1></div>
      </div>
      <InvoicingView />
    </div>
  ),
  contractors: () => <ContractorsList />,
  // The contractor contracts register (120), the Contractors page on its
  // Contracts tab - the list, or one contract (/manage/contracts/:id).
  contracts: () => <ContractorsList view="contracts" />,
  // Gordon, 24 October: Documentation "visible only to Admins and IT
  // contractors and editors. Move to Ticket Admin sub menu". The same
  // DocsPage staff and the portal draw; its links are /docs/:id, which for an
  // administrator is this tab as well (App.js), so it never leaves the hub.
  docs: () => <DocsPage />
};

/**
 * `isAdmin` false is the named approver who is not an administrator - the
 * case the /approvals route admits on purpose (App.js, isApprover). They get
 * the one tab that is theirs rather than a strip of doors that would each
 * answer 403.
 */
export default function ManageHub({ tab = 'dashboard', isAdmin = true, docs = false, catalogue = true }) {
  const location = useLocation();
  const Pane = PANES[tab] || PANES.dashboard;

  // Back to the top when the tab changes: a page scrolled halfway down
  // Contractors would otherwise open Approvals halfway down too, and the
  // strip is sticky so the reader would not see the switch.
  useEffect(() => { window.scrollTo(0, 0); }, [tab]);

  // Contracts (120) is part of Contractors, so the strip lights Contractors.
  const lit = (key) => tab === key || (key === 'contractors' && tab === 'contracts');
  const tabLink = (key, label) => (
    <NavLink key={key} to={MANAGE_TABS[key]} end
             className={`tab-button${lit(key) ? ' active' : ''}`}
             aria-current={lit(key) ? 'page' : undefined}>
      {label}
    </NavLink>
  );

  return (
    <div className="mh-page" data-tab={tab}>
      {/* STICKY, just under the navbar, like the portal's strip: the same
          --navbar-h that AppShell measures, the same page background so the
          rows scrolling under it do not show through (ManageHub.css). */}
      <div className="tabs-container mh-tabs" data-path={location.pathname}>
        <nav className="tabs" aria-label="Ticket management">
          {isAdmin ? (
            <>
              {tabLink('dashboard', 'Dashboard')}
              {tabLink('approvals', 'Approvals')}
              {tabLink('work-orders', 'Work orders')}
              <TabMenu
                label="Tickets"
                current={tab}
                items={[
                  { key: 'tickets', label: 'All tickets' },
                  { key: 'search', label: 'Advanced search' },
                  { key: 'templates', label: 'Templates' },
                  ...(catalogue || tab === 'catalogue' ? [{ key: 'catalogue', label: 'Catalogue' }] : [])
                ]}
              />
              <TabMenu
                label="Reporting"
                current={tab}
                items={[
                  { key: 'sla', label: 'SLA tracking' },
                  { key: 'analytics', label: 'Analytics' },
                  { key: 'invoicing', label: 'Invoicing' },
                  { key: 'reports', label: 'Monthly report' }
                ]}
              />
              {tabLink('contractors', 'Contractors')}
              {/* Last: reference, not a queue of work. Only while the docs
                  switch is on (and lit whenever it is the open tab). */}
              {(docs || tab === 'docs') && tabLink('docs', 'Docs')}
            </>
          ) : tabLink('approvals', 'Approvals')}
        </nav>
      </div>

      <div className="mh-pane">
        <Pane isAdmin={isAdmin} />
      </div>
    </div>
  );
}
